import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

/** Fields of ABTestUpdate the dialog changes. Variants and traffic split are left out on purpose. */
export function abTestPayload(form) {
  const num = (v, d) => (Number.isFinite(Number(v)) && String(v).trim() !== '' ? Number(v) : d);
  return {
    name: String(form.name || '').trim(),
    description: String(form.description || ''),
    confidence_level: num(form.confidence_level, 95),
    min_sample_size: Math.max(1, Math.round(num(form.min_sample_size, 100))),
    auto_optimization: !!form.auto_optimization,
  };
}

/*
  A/B testing: "Edit" — name, description, confidence level, minimum sample and
  auto-optimization (PUT /api/ab-tests/{id}). Variants and the traffic split
  are not changed here: changing them would mix the results already collected.
*/
export default function ABTestEditDialog({ test, open, onOpenChange, onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (test && open) {
      setForm({
        name: test.name || '',
        description: test.description || '',
        confidence_level: test.confidence_level ?? 95,
        min_sample_size: test.min_sample_size ?? 100,
        auto_optimization: !!test.auto_optimization,
      });
    }
  }, [test, open]);

  const save = async () => {
    const payload = abTestPayload(form);
    if (!payload.name) return;
    setSaving(true);
    try {
      const r = await axios.put(API + '/ab-tests/' + test.id, payload);
      toast.success(t('A/B test saved'));
      if (onSaved) onSaved(r.data || { ...test, ...payload });
      onOpenChange(false);
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail) || t('Could not save the A/B test'));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Edit A/B test')}</DialogTitle>
          <DialogDescription>
            {t('Variants and the traffic split cannot be changed: that would mix the results already collected. To try other variants, create a new test.')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="ab-edit-name">{t('Test name')}</Label>
            <Input id="ab-edit-name" data-testid="ab-edit-name" value={form.name || ''} onChange={set('name')} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ab-edit-description">{t('Test description')}</Label>
            <Textarea id="ab-edit-description" value={form.description || ''} onChange={set('description')} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ab-edit-confidence">{t('Confidence level, %')}</Label>
              <Input id="ab-edit-confidence" type="number" min="80" max="99.9" step="0.1" value={form.confidence_level ?? ''} onChange={set('confidence_level')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ab-edit-sample">{t('Minimum sample size')}</Label>
              <Input id="ab-edit-sample" type="number" min="1" value={form.min_sample_size ?? ''} onChange={set('min_sample_size')} />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <Label htmlFor="ab-edit-auto">{t('Auto-optimization')}</Label>
            <Switch id="ab-edit-auto" checked={!!form.auto_optimization} onCheckedChange={(v) => setForm((f) => ({ ...f, auto_optimization: v }))} />
          </div>
          {test && test.status === 'running' ? (
            <p className="text-xs text-muted-foreground">{t('The test is running: the changes apply right away.')}</p>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('Cancel')}
          </Button>
          <Button onClick={save} disabled={saving || !String(form.name || '').trim()} data-testid="ab-edit-save">
            {t('Save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
